import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../../../MobileModals.css';
import {
    faArrowUp,
    faArrowDown,
    faTrash,
    faSortAlphaDown,
    faTags,
    faHashtag
} from '@fortawesome/free-solid-svg-icons';
import { MASS_SONG_ROLES, DEFAULT_SONG_ROLE, MassRoleSelector } from '../../../../constants/songRoles';
import { useLanguage } from '../../../../contexts/LanguageContext';

function SetlistTab({ 
    songs = [], 
    selectedSongs = [], 
    setSelectedSongs, 
    searchTerm = '', 
    setSearchTerm,
    isMobile = false,
    communityId = null,
    eventData = null,
    songCollections = []
}) {
    const { t } = useLanguage();
    // State for filtered songs based on community
    const [filteredSongs, setFilteredSongs] = useState([]);
    
    // Add state for error messages
    const [error, setError] = useState(null);
    
    // Add state for selected setlist items
    const [selectedSetlistItems, setSelectedSetlistItems] = useState({});
    
    // Add state for sorting method
    const [sortMethod, setSortMethod] = useState('alphabet'); // 'alphabet' or 'category'

    // Tag filter state
    const [selectedTag, setSelectedTag] = useState('');
    
    // Set fixed height for mobile song list to show ~2 songs
    const MOBILE_LIST_HEIGHT = '160px';
    
    // Define the icon button style - desktop vs mobile
    const iconButtonStyle = isMobile ? {
        width: '30px',
        height: '30px',
        padding: '5px',
        margin: '0 3px',
        fontSize: '0.7rem'
    } : {
        width: '38px',
        height: '38px',
        padding: '8px',
        margin: '0 4px'
    };
    
    
    // Update filtered songs when songs array or communityId changes
    useEffect(() => {
        // If no community is selected, only show user's private songs
        if (!communityId) {
            const userSongs = songs.filter(song => song.owner_type === 'user');
            setFilteredSongs(userSongs);
            return;
        }
        
        // If community is selected, show only that community's songs + user's private songs
        const filtered = songs.filter(song =>
            // Community songs for this specific community
            (song.owner_type === 'community' && parseInt(song.owner_id) === parseInt(communityId)) ||
            // User's own songs
            (song.owner_type === 'user')
        );

        setFilteredSongs(filtered);
    }, [songs, communityId]);

    // Ensure each song in selectedSongs has an order property
    useEffect(() => {
        if (selectedSongs && selectedSongs.length > 0) {
            // Check if any songs are missing order property
            const needsOrderFix = selectedSongs.some(song => typeof song.order === 'undefined');
            
            if (needsOrderFix) {
                // Assign orders based on current array position
                const updatedSongs = selectedSongs.map((song, index) => ({
                    ...song,
                    order: index
                }));
                setSelectedSongs(updatedSongs);
            }
        }
    }, [selectedSongs, setSelectedSongs]);

    // Clear error after a delay
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);
    
    // Extract available tags from filtered songs
    const availableTags = (() => {
        const tagSet = new Set();
        filteredSongs.forEach(song => {
            if (song.tags) {
                song.tags.forEach(tag => tagSet.add(tag.name));
            }
        });
        return [...tagSet].sort();
    })();

    // Handle song search
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    // Sort and categorize songs based on selected method
    const getSortedAndCategorizedSongs = () => {
        // Filter songs based on search term
        let searchFiltered = filteredSongs.filter(song =>
            song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (song.composer && song.composer.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        // Filter by selected tag
        if (selectedTag) {
            searchFiltered = searchFiltered.filter(song =>
                song.tags && song.tags.some(tag => tag.name === selectedTag)
            );
        }

        if (sortMethod === 'alphabet') {
            // Simple alphabetical sorting - return as single category
            const sorted = [...searchFiltered].sort((a, b) =>
                a.title.toLowerCase().localeCompare(b.title.toLowerCase())
            );

            return { [t('events.modals.setlist.allSongs')]: sorted };
        } else {
            // Group by category/type and sort within each category
            const categorized = {};

            searchFiltered.forEach(song => {
                // Use song category if available, otherwise use 'Ostalo' (Other)
                const category = song.category || song.type || t('events.modals.setlist.other');
                
                if (!categorized[category]) {
                    categorized[category] = [];
                }
                categorized[category].push(song);
            });

            // Sort songs within each category alphabetically
            Object.keys(categorized).forEach(category => {
                categorized[category].sort((a, b) => 
                    a.title.toLowerCase().localeCompare(b.title.toLowerCase())
                );
            });

            return categorized;
        }
    };
    
    // Add a song to the setlist - ALLOW DUPLICATES
    const addSongToSetlist = (song) => {
        // Add the song (duplicates are allowed)
        const newOrder = selectedSongs.length;
        setSelectedSongs([...selectedSongs, {
            song_id: song.id,
            title: song.title,
            song_role: DEFAULT_SONG_ROLE,
            order: newOrder
        }]);
    };
    
    // Remove selected songs from the setlist
    const removeSelectedSongsFromSetlist = () => {
        const indicesToRemove = Object.keys(selectedSetlistItems)
            .filter(key => selectedSetlistItems[key])
            .map(key => parseInt(key.split('-').pop()))
            .sort((a, b) => b - a);
        
        if (indicesToRemove.length === 0) return;
        
        let updatedSongs = [...selectedSongs];
        indicesToRemove.forEach(index => {
            if (index >= 0 && index < updatedSongs.length) {
                updatedSongs.splice(index, 1);
            }
        });
        
        const reorderedSongs = updatedSongs.map((song, index) => ({
            ...song,
            order: index
        }));
        
        setSelectedSongs(reorderedSongs);
        setSelectedSetlistItems({});
    };
    
    // Handle setlist selection for move/delete operations
    const toggleSetlistSelection = (itemKey) => {
        setSelectedSetlistItems({
            ...selectedSetlistItems,
            [itemKey]: !selectedSetlistItems[itemKey]
        });
    };
    
    // Move selected songs up in the setlist
    const moveSelectedSongsUp = () => {
        const selectedIndices = Object.keys(selectedSetlistItems)
            .filter(key => selectedSetlistItems[key])
            .map(key => parseInt(key.split('-').pop()))
            .filter(index => !isNaN(index))
            .sort((a, b) => a - b);

        // Don't move if no items selected or if the topmost selected item is already at position 0
        if (selectedIndices.length === 0 || selectedIndices[0] === 0) return;

        const newSongs = [...selectedSongs];
        const newSelectedItems = {};

        // Move each selected item up by one position
        // Process from top to bottom to avoid conflicts
        for (const index of selectedIndices) {
            // Swap the song with the one above it
            [newSongs[index], newSongs[index - 1]] = [newSongs[index - 1], newSongs[index]];

            // Update the order properties
            const tempOrder = newSongs[index].order;
            newSongs[index].order = newSongs[index - 1].order;
            newSongs[index - 1].order = tempOrder;

            // Update selection to track the moved item
            const songId = newSongs[index - 1].song_id;
            newSelectedItems[`${songId}-${index - 1}`] = true;
        }

        setSelectedSongs(newSongs);
        setSelectedSetlistItems(newSelectedItems);
    };
    
    // Move selected songs down in the setlist
    const moveSelectedSongsDown = () => {
        const selectedIndices = Object.keys(selectedSetlistItems)
            .filter(key => selectedSetlistItems[key])
            .map(key => parseInt(key.split('-').pop()))
            .filter(index => !isNaN(index))
            .sort((a, b) => b - a);

        // Don't move if no items selected or if the bottommost selected item is already at the last position
        if (selectedIndices.length === 0 || selectedIndices[0] === selectedSongs.length - 1) return;

        const newSongs = [...selectedSongs];
        const newSelectedItems = {};

        // Move each selected item down by one position
        // Process from bottom to top to avoid conflicts
        for (const index of selectedIndices) {
            // Swap the song with the one below it
            [newSongs[index], newSongs[index + 1]] = [newSongs[index + 1], newSongs[index]];

            // Update the order properties
            const tempOrder = newSongs[index].order;
            newSongs[index].order = newSongs[index + 1].order;
            newSongs[index + 1].order = tempOrder;

            // Update selection to track the moved item
            const songId = newSongs[index + 1].song_id;
            newSelectedItems[`${songId}-${index + 1}`] = true;
        }

        setSelectedSongs(newSongs);
        setSelectedSetlistItems(newSelectedItems);
    };
    
    // Handle role change for a song
    const handleRoleChange = (songIndex, selectedRole) => {
        setSelectedSongs((prevSongs) =>
            prevSongs.map((song, index) =>
                index === songIndex
                    ? { ...song, song_role: selectedRole }
                    : song
            )
        );
    };
    
    // Check if the event is a Mass
    const isMassEvent = eventData && eventData.event_type === "mass";
    
    // Check if any setlist items are selected
    const hasSetlistSelections = Object.values(selectedSetlistItems).some(v => v);

    // Get categorized songs
    const categorizedSongs = getSortedAndCategorizedSongs();
    const categoryNames = Object.keys(categorizedSongs).sort();
    
    return (
        <div className="setlist-tab h-100 d-flex flex-column">
            <div className="row h-100">
                {/* Song Selection */}
                <div className="col-md-6 mb-3 song-selection-col d-flex flex-column">
                    {!isMobile && <h5>{t('events.modals.setlist.songbook')}</h5>}
                    
                    {/* Error message display */}
                    {error && (
                        <div className="alert alert-warning alert-dismissible py-2 mb-2" role="alert">
                            <small>{error}</small>
                            <button 
                                type="button" 
                                className="btn-close btn-close-sm" 
                                onClick={() => setError(null)}
                                style={{ fontSize: '0.7rem' }}
                            ></button>
                        </div>
                    )}
                    
                    {/* Search and Filter Controls */}
                    <div className={`d-flex gap-1 mb-3 ${isMobile ? 'flex-column' : ''}`}>
                        <input
                            type="text"
                            className={`form-control ${isMobile ? 'form-control-sm mb-1' : ''}`}
                            placeholder={t('events.modals.setlist.searchSongs')}
                            value={searchTerm}
                            onChange={handleSearch}
                            style={{ flex: isMobile ? '1' : '2' }}
                        />

                        {/* Tag filter */}
                        {availableTags.length > 0 && (
                            <div className="input-group" style={{ flex: '1', maxWidth: isMobile ? '100%' : '180px' }}>
                                <span className={`input-group-text ${isMobile ? 'input-group-text-sm' : ''}`}>
                                    <FontAwesomeIcon icon={faHashtag} />
                                </span>
                                <select
                                    className={`form-select ${isMobile ? 'form-select-sm' : ''}`}
                                    value={selectedTag}
                                    onChange={(e) => setSelectedTag(e.target.value)}
                                >
                                    <option value="">{t('songbook.filter.allTags')}</option>
                                    {availableTags.map(tag => (
                                        <option key={tag} value={tag}>#{tag}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Sort method buttons */}
                        <div className="btn-group" role="group" style={{ flex: 'none' }}>
                            <button
                                type="button"
                                className={`btn ${sortMethod === 'alphabet' ? 'btn-primary' : 'btn-outline-primary'} ${isMobile ? 'btn-sm' : ''}`}
                                onClick={() => setSortMethod('alphabet')}
                                title={t('events.modals.setlist.sortAlphabet')}
                            >
                                <FontAwesomeIcon icon={faSortAlphaDown} />
                            </button>
                            <button
                                type="button"
                                className={`btn ${sortMethod === 'category' ? 'btn-primary' : 'btn-outline-primary'} ${isMobile ? 'btn-sm' : ''}`}
                                onClick={() => setSortMethod('category')}
                                title={t('events.modals.setlist.sortCategory')}
                            >
                                <FontAwesomeIcon icon={faTags} />
                            </button>
                        </div>
                    </div>
                    
                    {communityId === null && (
                        <div className="form-text mb-1 text-warning">
                            <strong>{t('common.note')}:</strong> {t('events.modals.setlist.notePrivate')}
                        </div>
                    )}
                    
                    {/* Song list with fixed height and scroll */}
                    <div className="list-group song-list flex-grow-1" style={{ 
                        height: isMobile ? MOBILE_LIST_HEIGHT : '0',
                        minHeight: isMobile ? MOBILE_LIST_HEIGHT : '400px',
                        maxHeight: isMobile ? MOBILE_LIST_HEIGHT : 'none',
                        overflowY: 'auto',
                        flexShrink: 0
                    }}>
                        {categoryNames.length === 0 ? (
                            <div className="text-center py-3 text-muted">
                                {searchTerm ? t('events.modals.setlist.noResults') : t('events.modals.setlist.noSongsAvailable')}
                            </div>
                        ) : (
                            categoryNames.map(categoryName => (
                                <div key={categoryName}>
                                    {/* Category header - only show if using category sorting and there are multiple categories */}
                                    {sortMethod === 'category' && categoryNames.length > 1 && (
                                        <div className="list-group-item list-group-item-secondary">
                                            <strong>{categoryName}</strong>
                                        </div>
                                    )}
                                    
                                    {/* Songs in this category */}
                                    {categorizedSongs[categoryName].map(song => {
                                        return (
                                            <div
                                                key={song.id}
                                                className="list-group-item list-group-item-action d-flex align-items-center py-1"
                                            >
                                              <div
                                                className="flex-grow-1"
                                                onClick={() => addSongToSetlist(song)}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {/* Song title restricted to one line */}
                                                <div className="song-title-single-line">
                                                  {song.title}
                                                </div>
                                                {/* Composer on the next line */}
                                                {song.composer && (
                                                  <small className="text-muted">{song.composer}</small>
                                                )}
                                              </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Selected Songs */}
                <div className="col-md-6 mb-3 selected-songs-col d-flex flex-column">
                    <h5 className={isMobile ? "h6 mb-1" : "h5"}>{t('events.modals.setlist.selectedSongs')}</h5>

                    {selectedSongs.length === 0 ? (
                        <div className="alert alert-info py-2">
                            {t('events.modals.setlist.noSelectedSongs')}
                        </div>
                    ) : (
                        <div className="d-flex flex-column h-100">
                            {/* Action buttons above the setlist */}
                            <div className="d-flex justify-content-center align-items-center mb-3 gap-3">
                                <button
                                    type="button"
                                    className={`btn btn-outline-secondary rounded-circle ${isMobile ? 'btn-sm' : ''}`}
                                    onClick={moveSelectedSongsUp}
                                    disabled={!hasSetlistSelections}
                                    title={t('events.modals.setlist.moveUp')}
                                    style={{
                                        width: isMobile ? '32px' : '40px',
                                        height: isMobile ? '32px' : '40px',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                </button>
                                
                                <button
                                    type="button"
                                    className={`btn btn-outline-secondary rounded-circle ${isMobile ? 'btn-sm' : ''}`}
                                    onClick={moveSelectedSongsDown}
                                    disabled={!hasSetlistSelections}
                                    title={t('events.modals.setlist.moveDown')}
                                    style={{
                                        width: isMobile ? '32px' : '40px',
                                        height: isMobile ? '32px' : '40px',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faArrowDown} />
                                </button>
                                
                                <button
                                    type="button"
                                    className={`btn btn-outline-danger rounded-circle ${isMobile ? 'btn-sm' : ''}`}
                                    onClick={removeSelectedSongsFromSetlist}
                                    disabled={!hasSetlistSelections}
                                    title={t('events.modals.setlist.removeSelected')}
                                    style={{
                                        width: isMobile ? '32px' : '40px',
                                        height: isMobile ? '32px' : '40px',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                            
                            {/* Setlist with fixed height and scroll */}
                            <div className="list-group setlist flex-grow-1" style={{ 
                                height: isMobile ? MOBILE_LIST_HEIGHT : '0',
                                minHeight: isMobile ? MOBILE_LIST_HEIGHT : '400px',
                                maxHeight: isMobile ? MOBILE_LIST_HEIGHT : 'none',
                                overflowY: 'auto',
                                flexShrink: 0
                            }}>
                                {selectedSongs.map((song, index) => (
                                    <div
                                        key={`setlist-${song.song_id}-${index}`}
                                        className="list-group-item d-flex align-items-center flex-nowrap py-1"
                                    >
                                        {/* Checkbox */}
                                        <div className="form-check me-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={!!selectedSetlistItems[`${song.song_id}-${index}`]}
                                                onChange={() => toggleSetlistSelection(`${song.song_id}-${index}`)}
                                                id={`setlist-item-${song.song_id}-${index}`}
                                            />
                                            <label className="form-check-label sr-only" htmlFor={`setlist-item-${song.song_id}-${index}`}>
                                                Select {song.title}
                                            </label>
                                        </div>

                                        {/* Song Title */}
                                        <div className="me-2 flex-grow-1 song-title-single-line">
                                            {song.title}
                                        </div>

                                        {/* Song role selector */}
                                        {isMassEvent ? (
                                            <MassRoleSelector
                                                className="form-select-sm role-selector"
                                                style={{ width: isMobile ? '90px' : 'auto', minWidth: isMobile ? '90px' : '120px' }}
                                                value={song.song_role || ''}
                                                onChange={(e) => handleRoleChange(index, e.target.value)}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                className="form-control form-control-sm role-input"
                                                style={{ width: isMobile ? '90px' : 'auto', minWidth: isMobile ? '90px' : '120px' }}
                                                value={song.song_role || ''}
                                                onChange={(e) => handleRoleChange(index, e.target.value)}
                                                placeholder={t('events.modals.setlist.role')}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>


            {communityId === null && selectedSongs.length > 0 && (
                <div className="alert alert-warning py-1 small">
                    <strong>{t('common.warning')}:</strong> {t('events.modals.setlist.warningNoGroup')}
                </div>
            )}
        </div>
    );
}

export default SetlistTab;